import { useCallback } from "react";
import notify from "devextreme/ui/notify";
import { flatObject } from "../../../utils/objectUtils.js";
import { getParameterFromUrl } from "../utils/urlUtils.js";
import { downloadFile, buildDraftPayload, getEncryptionKey } from "../services/formFileService.js";
import { postFormToServer } from "../services/formApiService.js";

export default function useFormSubmit({
  docRef, templateRef, settingsRef, formElementRef,
  state, updateState,
  clearUnvisibleData, executeFunctionByUid, ExecuteMultipleFunctions,
  createUser, validateAndNotify,
}) {
  const encryptionKey = getEncryptionKey();

  const sendForm = useCallback((e) => {
    try {
      const res = e.validationGroup.validate();

      if (res.status === "valid") {
        const validationTriggered = validateAndNotify();
        if (validationTriggered) return;

        clearUnvisibleData();
        executeFunctionByUid("RunBeforeSend");

        if (templateRef.current.RunBeforeSend !== undefined) {
          ExecuteMultipleFunctions(templateRef.current.RunBeforeSend);
        }

        if (!docRef.current.apendix) docRef.current.apendix = {};
        docRef.current.apendix.marketer =
          (state.user?.firstName || "") + " " + (state.user?.lastName || "");

        const form = formElementRef.current;

        // Strip File objects from docRef before JSON serialization — File serializes as {}
        // which causes a server-side DataTable type error. Files are sent separately via FormData.
        const docForJson = JSON.parse(JSON.stringify(docRef.current, (key, val) => {
          if (val instanceof File) return undefined;
          if (Array.isArray(val) && val.length > 0 && val[0] instanceof File) return undefined;
          return val;
        }));

        // Security: do not submit if encryption fails — plain data must never reach the server
        const encryptedJson = encryptionKey
          ? encryptionKey.encrypt(JSON.stringify(docForJson))
          : null;
        if (!encryptedJson) {
          console.error("sendForm: encryption failed");
          notify("שגיאה בהצפנה, לא ניתן לשלוח", "error", 3000);
          return;
        }

        const sendInput = {
          flatValues: flatObject(docForJson),
          document: docForJson,
          user: createUser(),
          formId: state.dataFromDB.ID,
          XLClient: state.XLClient,
          XLAccount: state.XLAccount,
          jsonFile: encryptedJson,
          sourceTaskId: getParameterFromUrl("t"),
          sendingType: state.sendingType,
          formVersion: "hogery-form",
          draftPayload: docRef.current.chosenAgentId != null
            ? buildDraftPayload(docRef.current, {
                taskId: getParameterFromUrl("t"),
                formId: getParameterFromUrl("f"),
                formName: state.dataFromDB.FormName,
                status: undefined,
                draftName: state.draftName,
              })
            : null,
          chosenAgentId: docRef.current.chosenAgentId,
        };

        downloadFile(docRef.current, state.dataFromDB.FormName);

        // Clean up stale jsonValues inputs from previous submit attempts
        form.querySelectorAll('input[name="jsonValues"]').forEach(el => el.remove());

        const input = document.createElement("input");
        input.setAttribute("name", "jsonValues");
        input.setAttribute("value", JSON.stringify(sendInput));
        input.setAttribute("type", "hidden");

        form.appendChild(input);
        const data = new FormData(form);

        // File objects live in docRef.current[tabId][fieldUid] (or [tabId][idx][fieldUid]
        // for array tabs). They are not reflected back into the DOM <input type="file">,
        // so new FormData(form) misses them — append manually under "fileUploader{uid}".
        Object.entries(docRef.current).forEach(([tabId, tabVal]) => {
          if (!tabVal || typeof tabVal !== "object") return;
          if (Array.isArray(tabVal)) {
            // array tab: docRef[tabId] = [{fieldUid: File, ...}, ...]
            tabVal.forEach((instance) => {
              if (!instance || typeof instance !== "object") return;
              Object.entries(instance).forEach(([uid, val]) => {
                const key = "fileUploader" + uid;
                if (Array.isArray(val)) {
                  val.forEach(f => { if (f instanceof File) data.append(key, f); });
                } else if (val instanceof File) {
                  data.append(key, val);
                }
              });
            });
          } else {
            // regular tab: docRef[tabId] = {fieldUid: File, ...}
            Object.entries(tabVal).forEach(([uid, val]) => {
              const key = "fileUploader" + uid;
              if (Array.isArray(val)) {
                val.forEach(f => { if (f instanceof File) data.append(key, f); });
              } else if (val instanceof File) {
                data.append(key, val);
              }
            });
          }
        });

        updateState({ isFirsTimeLoaded: false, isLoading: true });

        postFormToServer(data)
          .then((result) => {
            if (result.ok) {
              if (docRef.current.files?.toReference) {
                notify({ message: "הפרטים שמילאת נשמרו והמסמך עבר להמשך מילוי" }, "success", 3000);
                window.location.hash = "#/home";
                return;
              }
              if (result.response) {
                window.location.hash = `#/signatures?i=${result.response}`;
              }
            } else {
              if (result.errorMessage === "המסמך לא אושר לשליחה חוזרת") {
                notify(result.errorMessage, "error", 8000);
              } else {
                if (!navigator.onLine) {
                  notify("משהו השתבש.. אנא בדוק חיבור לאינטרנט", "error", 5000);
                }
                notify("משהו השתבש .. נסה שנית", "error", 3000);
              }
              updateState({ isLoading: false });
            }
          })
          .catch((err) => {
            console.error("postFormToServer error:", err);
            notify("שגיאה בשליחת הטופס, נסה שנית", "error", 5000);
            updateState({ isLoading: false, isFirsTimeLoaded: true });
          });
      } else {
        const msg = "אופס... חלה שגיאה בפרטים שמילאת";
        notify(msg, "error", 3000);
      }
    } catch (err) {
      console.error("sendForm error:", err);
      notify("שגיאה בשליחת הטופס, נסה שנית", "error", 5000);
      updateState({ isLoading: false, isFirsTimeLoaded: true });
    }
  }, [
    docRef, templateRef, formElementRef, state, updateState,
    clearUnvisibleData, executeFunctionByUid, ExecuteMultipleFunctions,
    createUser, validateAndNotify, encryptionKey,
  ]);

  return { sendForm };
}
